import React, { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import {
    Button,
    Checkbox,
    CloseIcon,
    Dialog,
    Dropdown,
    Flex,
    InputLabel,
    teamsTheme,
} from "@fluentui/react-northstar";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { MIN_DATE } from "../../../../utils/dateValidation";
import {
    FluentProvider,
    Tab,
    TabList,
    teamsLightTheme,
} from "@fluentui/react-components";
import {
    EXPORT_COMPARE_BY,
    EXPORT_DETAIL_GROUPS,
    EXPORT_FORMATS,
    EXPORT_SYNTHESE_OPTIONS,
} from "../../constants";
import { EXPORT_TABS } from "../../hooks/useExportDialog";
import { DEVICE_MAX } from "../../../../constants/responsive";

/** Multi-sélection avec recherche (Département / Agence / Manager / Collaborateurs) */
const MultiSelect = ({
    label,
    items,
    value,
    onChange,
    placeholder,
    loading,
}) => {
    /* `positionFixed` fait calculer le `width: 100%` de la liste par rapport au viewport :
       on la cale sur la largeur réelle de la zone de saisie (ancre du popper), mesurée à l'ouverture */
    const fieldRef = useRef(null);
    const [listWidth, setListWidth] = useState();

    return (
        <Field ref={fieldRef}>
            <InputLabel content={label} />
            <Dropdown
                multiple
                search
                fluid
                items={items}
                value={items.filter((i) => value.includes(i.key))}
                onChange={(_, data) =>
                    onChange((data?.value || []).map((i) => i.key))
                }
                placeholder={value.length ? "Ajouter…" : placeholder}
                noResultsMessage="Aucun résultat"
                loading={loading}
                loadingMessage="Chargement…"
                positionFixed
                list={{ styles: { width: listWidth } }}
                onOpenChange={(_, data) => {
                    if (data?.open && fieldRef.current) {
                        const anchor =
                            fieldRef.current.querySelector(
                                ".ui-dropdown__container"
                            ) ?? fieldRef.current;
                        /* en px explicite : le renderer fela n'ajoute pas l'unité */
                        setListWidth(
                            `${anchor.getBoundingClientRect().width}px`
                        );
                    }
                }}
            />
        </Field>
    );
};

const ExportDialog = ({ logic }) => {
    const {
        dialog,
        tab,
        form,
        periode,
        perimetre,
        colonnes,
        exportData,
        canExport,
        isExporting,
    } = logic;
    const { control, watch } = form;
    const [startDate, endDate] = watch(["start_date", "end_date"]);

    /* Dropdown simple dont la valeur du formulaire est la `key` de l'item */
    const renderSelect = (items) =>
        function SelectField({ field }) {
            return (
                <Dropdown
                    fluid
                    checkable
                    items={items}
                    value={items.find((i) => i.key === field.value)}
                    onChange={(_, data) =>
                        data?.value && field.onChange(data.value.key)
                    }
                />
            );
        };

    const renderMultiSelect = (props) =>
        function MultiSelectField({ field }) {
            return (
                <MultiSelect
                    {...props}
                    value={field.value}
                    onChange={field.onChange}
                />
            );
        };

    return (
        <Dialog
            open={dialog.open}
            header="Exporter les données"
            headerAction={{ icon: <CloseIcon />, onClick: dialog.close }}
            onCancel={dialog.close}
            confirmButton={{
                content: "Exporter les données",
                flat: true,
                onClick: exportData,
                disabled: !canExport,
                loading: isExporting,
            }}
            styles={{ width: "660px", maxWidth: "100%" }}
            content={
                /* La Dialog northstar est rendue dans un portal hors du FluentProvider de l'app :
                   on le réinjecte pour que les composants v9 (Tabs, DatePicker) aient leurs tokens */
                <V9Provider theme={teamsLightTheme}>
                    <Flex column gap="gap.medium">
                        {/* ---- Période et format ---- */}
                        <BlockTitle>Période et format</BlockTitle>
                        <Row3>
                            <Field>
                                <InputLabel content="À partir du" />
                                <Controller
                                    name="start_date"
                                    control={control}
                                    render={({ field }) => (
                                        <SDatePicker
                                            strings={periode.localizedStrings}
                                            formatDate={periode.formatDate}
                                            value={field.value}
                                            onSelectDate={field.onChange}
                                            minDate={MIN_DATE}
                                            maxDate={endDate}
                                            placeholder="jj/mm/aaaa"
                                            allowTextInput
                                            isMonthPickerVisible={false}
                                        />
                                    )}
                                />
                            </Field>
                            <Field>
                                <InputLabel content="Jusqu'au" />
                                <Controller
                                    name="end_date"
                                    control={control}
                                    render={({ field }) => (
                                        <SDatePicker
                                            strings={periode.localizedStrings}
                                            formatDate={periode.formatDate}
                                            value={field.value}
                                            onSelectDate={field.onChange}
                                            minDate={startDate || MIN_DATE}
                                            placeholder="jj/mm/aaaa"
                                            allowTextInput
                                            isMonthPickerVisible={false}
                                        />
                                    )}
                                />
                            </Field>
                            <Field>
                                <InputLabel content="Format" />
                                <Controller
                                    name="export_format"
                                    control={control}
                                    render={renderSelect(EXPORT_FORMATS)}
                                />
                            </Field>
                        </Row3>

                        {/* ---- Périmètre ---- */}
                        <BlockTitle>Périmètre</BlockTitle>
                        <Row3>
                            <Controller
                                name="department_ids"
                                control={control}
                                render={renderMultiSelect({
                                    label: "Département",
                                    items: perimetre.departements.items,
                                    loading: perimetre.departements.isLoading,
                                    placeholder: "Tous",
                                })}
                            />
                            <Controller
                                name="agency_ids"
                                control={control}
                                render={renderMultiSelect({
                                    label: "Agence",
                                    items: perimetre.agences.items,
                                    loading: perimetre.agences.isLoading,
                                    placeholder: "Toutes",
                                })}
                            />
                            <Controller
                                name="manager_ids"
                                control={control}
                                render={renderMultiSelect({
                                    label: "Manager",
                                    items: perimetre.managers.items,
                                    loading: perimetre.managers.isLoading,
                                    placeholder: "Tous",
                                })}
                            />
                        </Row3>

                        {/* ---- Onglets ---- */}
                        <TabList
                            selectedValue={tab.value}
                            onTabSelect={(_, data) => tab.set(data.value)}
                        >
                            <Tab value={EXPORT_TABS.synthese}>Synthèse</Tab>
                            <Tab value={EXPORT_TABS.detail}>
                                Détail collaborateurs
                            </Tab>
                        </TabList>

                        {tab.value === EXPORT_TABS.synthese && (
                            <>
                                <CompareField>
                                    <InputLabel content="Comparer par" />
                                    <Controller
                                        name="compare_by"
                                        control={control}
                                        render={renderSelect(EXPORT_COMPARE_BY)}
                                    />
                                </CompareField>
                                <Checks>
                                    {EXPORT_SYNTHESE_OPTIONS.map((opt) => (
                                        <Controller
                                            key={opt.key}
                                            name={`sections.${opt.key}`}
                                            control={control}
                                            render={({ field }) => (
                                                <Checkbox
                                                    label={opt.label}
                                                    checked={!!field.value}
                                                    onChange={() =>
                                                        field.onChange(
                                                            !field.value
                                                        )
                                                    }
                                                />
                                            )}
                                        />
                                    ))}
                                </Checks>
                            </>
                        )}

                        {tab.value === EXPORT_TABS.detail && (
                            <>
                                <MultiSelect
                                    label="Collaborateurs concernés"
                                    items={perimetre.collaborateursDisponibles}
                                    value={perimetre.collaborateurs}
                                    onChange={perimetre.setCollaborateurs}
                                    placeholder="Tous les collaborateurs du périmètre"
                                    loading={perimetre.isLoadingCollaborateurs}
                                />
                                <Checks>
                                    {EXPORT_DETAIL_GROUPS.map((group) => (
                                        <React.Fragment key={group.key}>
                                            <GroupTitle>
                                                {group.label}
                                                {group.toggleable && (
                                                    <Button
                                                        text
                                                        size="small"
                                                        content={
                                                            colonnes.isGroupAllChecked(
                                                                group
                                                            )
                                                                ? "Tout désélectionner"
                                                                : "Tout sélectionner"
                                                        }
                                                        onClick={() =>
                                                            colonnes.toggleGroup(
                                                                group
                                                            )
                                                        }
                                                        styles={{
                                                            color: "var(--prh-violet)",
                                                            minWidth: 0,
                                                            padding: 0,
                                                        }}
                                                    />
                                                )}
                                            </GroupTitle>
                                            {group.options.map((opt) => (
                                                <Checkbox
                                                    key={opt.key}
                                                    label={opt.label}
                                                    checked={
                                                        !!colonnes.detail[
                                                            opt.key
                                                        ]
                                                    }
                                                    onChange={() =>
                                                        colonnes.toggleDetail(
                                                            opt.key
                                                        )
                                                    }
                                                />
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </Checks>
                            </>
                        )}
                    </Flex>
                </V9Provider>
            }
        />
    );
};

export default ExportDialog;

const V9Provider = styled(FluentProvider)`
    background-color: transparent;
`;

const BlockTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: var(--prh-ink);
`;

const Row3 = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    @media ${DEVICE_MAX.tablet} {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const CompareField = styled(Field)`
    max-width: calc((100% - 28px) / 3);
    @media ${DEVICE_MAX.tablet} {
        max-width: none;
    }
`;

/* Mêmes couleurs que les Dropdown northstar (fond + hover) */
const { colorScheme } = teamsTheme.siteVariables;
const SDatePicker = styled(DatePicker)`
    && {
        background-color: ${colorScheme.default.background2};
        border-color: transparent;
        border-radius: 4px;
        padding-left: 0;
    }
    &&:hover {
        background-color: ${colorScheme.default.backgroundHover3};
        border-color: transparent;
    }
    && input {
        background-color: transparent;
        padding-left: 0.8571rem;
    }
`;

const Checks = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px 20px;
    padding: 4px 0;
    @media ${DEVICE_MAX.tablet} {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const GroupTitle = styled.div`
    grid-column: 1 / -1;
    font-size: 13px;
    font-weight: 600;
    color: var(--prh-muted);
    margin-top: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;
